/**
 * @file dashboard-counters.ts
 * @description Реестр плиток «Обзора склада» (phase19.24 этап 1, шаг 3;
 * phase19.31 шаг О4 — подписи по утверждённому макету (в2)).
 *
 * Плитка — это задача, а не витрина: у каждой есть подпись, пояснение, тон и
 * адрес ОТФИЛЬТРОВАННОГО реестра, куда кладовщик уходит работать. Всё это —
 * данные, а не ветки в разметке: седьмая плитка добавляется записью в
 * `DASHBOARD_COUNTERS` (OCP, docs/processes/engineering-principles.md).
 *
 * Ссылка считается от выбранного на сводке склада (`warehouseId`): реестр
 * открывается с тем же отбором, что был на сводке.
 *
 * Тон зависит от значения: «Недостача 0» красным тревожит на пустом месте,
 * поэтому цвет включается только при ненулевом счётчике (`counterTone`).
 * Пояснение склоняется по числу: «1 партия без ячейки», «4 партии без ячейки».
 */

import {
  AlertTriangle,
  CalendarClock,
  ClipboardList,
  FilePen,
  MapPinOff,
  TrendingDown,
  type LucideIcon,
} from "lucide-react";

import { wmsHref } from "./dashboard-href";
import type { WmsDashboardCountersDto } from "../../../lib/api-wms-dashboard";
import { pluralRu } from "../../../lib/plural";

/** Тон плитки: спокойный, «успеть до» и «работа стоит». */
export type CounterTone = "default" | "warning" | "danger";

/** Откуда считается адрес плитки. */
export interface DashboardCounterHrefParams {
  /** База маршрута WMS: `/shop/:slug/wms`. */
  base: string;
  /** Склад, выбранный на сводке; пустая строка — все склады магазина. */
  warehouseId: string;
}

/** Одна плитка сводки: что считаем, как подписано и куда ведёт. */
export interface DashboardCounterMeta {
  /** Ключ счётчика в ответе сервера. */
  key: keyof WmsDashboardCountersDto;
  /** Короткая подпись плитки (обычным регистром). */
  label: string;
  icon: LucideIcon;
  /** Тон при ненулевом значении; ноль всегда нейтрален (см. `counterTone`). */
  activeTone: CounterTone;
  /** Адрес отфильтрованного реестра — со складом сводки. */
  href: (params: DashboardCounterHrefParams) => string;
  /** Что означает число — строка под числом, склоняется по нему. */
  hint: (value: number) => string;
  /** Подсказка на наведении, когда короткой подписи плитки мало. */
  title?: string;
}

/** Пояснение «<число-форма> <хвост>»: формы — для 1, 2–4 и 5+. */
function forms(one: string, few: string, many: string): (value: number) => string {
  return (value) => pluralRu(value, [one, few, many]);
}

/**
 * Порядок реестра = порядок плиток на экране: сначала работа на сегодня
 * (сборка), потом то, что мешает складу быть точным.
 */
export const DASHBOARD_COUNTERS: DashboardCounterMeta[] = [
  {
    key: "pickingOpen",
    label: "К сборке",
    icon: ClipboardList,
    activeTone: "default",
    href: ({ base, warehouseId }) => wmsHref(base, "/picking", { wh: warehouseId }),
    hint: forms("задание ждёт сборки", "задания ждут сборки", "заданий ждут сборки"),
  },
  {
    key: "pickingShortage",
    label: "Недостача",
    icon: AlertTriangle,
    activeTone: "danger",
    href: ({ base, warehouseId }) => wmsHref(base, "/picking", { tab: "shortage", wh: warehouseId }),
    hint: forms("задание ждёт решения", "задания ждут решения", "заданий ждут решения"),
  },
  {
    key: "unplacedLots",
    label: "Без места",
    icon: MapPinOff,
    activeTone: "warning",
    // `select=all`: партии без места уже отмечены, и кладовщику остаётся
    // нажать «Разместить выбранные» (phase19.24 этап 1, шаг 4).
    href: ({ base, warehouseId }) => wmsHref(base, "/stock", { unplaced: "1", select: "all", wh: warehouseId }),
    hint: forms("партия без ячейки", "партии без ячейки", "партий без ячейки"),
  },
  {
    key: "draftReceipts",
    label: "Черновики",
    icon: FilePen,
    // Решение пользователя 2026-09-21: счётчик — только приёмки; зависшие
    // перемещения и списания — строками «Что нужно сделать».
    title: "Черновики приёмок; зависшие перемещения и списания — в блоке «Что нужно сделать»",
    activeTone: "default",
    href: ({ base, warehouseId }) => wmsHref(base, "/receipts", { status: "DRAFT", wh: warehouseId }),
    hint: forms("приёмка не проведена", "приёмки не проведены", "приёмок не проведено"),
  },
  {
    key: "expiringLots",
    label: "Срок",
    icon: CalendarClock,
    activeTone: "warning",
    href: ({ base, warehouseId }) => wmsHref(base, "/expiring", { wh: warehouseId }),
    hint: forms("партия истекает за 30 дней", "партии истекают за 30 дней", "партий истекает за 30 дней"),
  },
  {
    key: "belowMinSkus",
    label: "Мало",
    icon: TrendingDown,
    activeTone: "warning",
    // `positive=0`: нулевой остаток — самый тревожный случай «ниже минимума»,
    // а отбор остатков по умолчанию прячет нули (TD-58).
    href: ({ base, warehouseId }) => wmsHref(base, "/stock", { belowMin: "1", positive: "0", wh: warehouseId }),
    hint: forms("фасовку пора пополнить", "фасовки пора пополнить", "фасовок пора пополнить"),
  },
];

/** Тон плитки по значению: ноль — всегда нейтральный. */
export function counterTone(meta: DashboardCounterMeta, value: number): CounterTone {
  return value > 0 ? meta.activeTone : "default";
}
